/**
 * The horizontal identity band on, over, or under a media frame.
 *
 * Three placements, one component, because the difference between a caption
 * sitting on a poster, a dock overlapping the artwork's lower edge and a band
 * below the frame is where it sits — not what it contains. Each placement
 * carries its own scrim decision, and only the two that sit ON media get one.
 *
 * The `progress` slot runs BEHIND the strip's own content rather than beside
 * it, so a playing row shows its position without giving up a column. It is an
 * absolutely-positioned layer, not a flex child: as a child it either took
 * horizontal space away from the identity or painted over the lead and
 * trailing zones, and neither is a bed.
 *
 * `OverlappingMedia` carries its own surface. The lift alone leaves a
 * transparent block riding a photograph, which is text on a picture; and the
 * lift alone at full bleed leaves a capsule with no inline inset, so its
 * corners never read as corners.
 *
 * The primary line takes the item's open control, exactly as
 * `CresultcardIdentity`'s title does. Without it a strip could only ever be a
 * label: every item whose name lives in an overlay capsule had a pointer target
 * and NO keyboard tab stop, and three families each answered that by building
 * their own capsule out of `CresultcardIdentity` and a hand-mixed surface. The
 * strip is the composition the reference designs are drawn on, so the control
 * belongs here rather than in three private substitutes for it.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import { useCrcTouchExpansion } from "./cresultcard.touch";
import {
  CRC_CORE_BACKGROUND_COLOR,
  CRC_MEDIA_SCRIM_IMAGE,
  CRC_TEXT,
  CRC_TEXT_MUTED,
  CRC_TILE_SHADOW,
  CRC_TRUNCATE_STYLE,
  crcTextClampStyle,
} from "./cresultcard.tokens";
import {
  CresultcardStripPlacement,
  CresultcardTitleLines,
} from "./cresultcard.types";
import { CresultcardFactStrip } from "./CresultcardFactStrip";
import type {
  CresultcardFactSpec,
  CresultcardOpenControl,
} from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";

/** How far the overlapping placement rides up over the frame's lower edge. */
const OVERLAP_OFFSET = -18;
/** The capsule's own inline inset, so it reads as a block ON the frame. */
const OVERLAP_INLINE_INSET = 11;
const STRIP_GAP = 10;
/** The primary line is the item's one control, and the kit stamps it as such. */
const PRIMARY_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/IdentityStrip/PrimaryTrigger",
  "data-crc-control": true,
  "data-crc-title": true,
};
const PRIMARY_ROOT_ROLE = "button";
/**
 * The primary is read along a strip, so its surface starts on the leading edge.
 * These reach `Ccontainer` as props, where `Cbutton` defaults them to centring,
 * and no `textAlign` in a style can outrank them.
 */
const PRIMARY_ALIGN_ITEMS = "flex-start";
const PRIMARY_JUSTIFY_CONTENT = "flex-start";
const PRIMARY_CONTROL_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  minWidth: 0,
  textAlign: "start",
};

interface CresultcardIdentityStripProps {
  /** A badge, a rank, a piece of art. */
  readonly lead?: ReactNode;
  readonly primary: string;
  /** How many lines the primary may take. One line, cut, unless declared. */
  readonly primaryLines?: CresultcardTitleLines;
  /**
   * Present when this item opens something. Then the PRIMARY is the control —
   * the strip's one tab stop, carrying the accessible name, `aria-expanded` and
   * `aria-controls`, on the same contract as `CresultcardIdentity`'s title.
   */
  readonly openControl?: CresultcardOpenControl;
  /**
   * `ReactNode`, not `string`: the line under the name is a venue with a score,
   * a source with an age, a formatted figure — composed nodes in every case a
   * family actually has — and a `string` type is what sent three of them to
   * build their own capsule instead.
   */
  readonly secondary?: ReactNode;
  /** The one figure the strip carries — a price, a duration, a score. */
  readonly figure?: ReactNode;
  /** Two to four discrete cells. Every one of them survives. */
  readonly facts?: readonly CresultcardFactSpec[];
  readonly trailing?: ReactNode;
  readonly placement: CresultcardStripPlacement;
  /** A track running behind the content, not beside it. */
  readonly progress?: ReactNode;
}

export function CresultcardIdentityStrip({
  lead,
  primary,
  primaryLines = CresultcardTitleLines.One,
  openControl,
  secondary,
  figure,
  facts,
  trailing,
  placement,
  progress,
}: CresultcardIdentityStripProps): ReactElement {
  const overMedia = placement === CresultcardStripPlacement.OverMedia;
  const overlapping =
    placement === CresultcardStripPlacement.OverlappingMedia;
  const nestedRadius = useCresultcardNestedRadius();
  const { isMobile } = useCresultcardViewport();
  const primaryTouch = useCrcTouchExpansion({ isMobile });

  const primaryNode = (
    <span
      style={{
        ...crcTextClampStyle({ lines: primaryLines }),
        minWidth: 0,
        fontSize: 12,
        fontWeight: 720,
        letterSpacing: "-0.015em",
        lineHeight: 1.2,
        color: CRC_TEXT,
      }}
    >
      {primary}
    </span>
  );

  return (
    <div
      data-el="Cresultcard/IdentityStrip/Root"
      data-crc-placement={placement}
      style={{
        position: overMedia ? "absolute" : "relative",
        insetInline: overMedia ? 0 : undefined,
        insetBlockEnd: overMedia ? 0 : undefined,
        marginBlockStart: overlapping ? OVERLAP_OFFSET : undefined,
        marginInline: overlapping ? OVERLAP_INLINE_INSET : undefined,
        minWidth: 0,
        overflow: overlapping ? "hidden" : undefined,
        borderRadius: overlapping ? nestedRadius : undefined,
        backgroundColor: overlapping ? CRC_CORE_BACKGROUND_COLOR : undefined,
        backgroundImage: overMedia ? CRC_MEDIA_SCRIM_IMAGE : undefined,
        boxShadow: overlapping ? CRC_TILE_SHADOW : undefined,
      }}
    >
      {progress != null && (
        <span
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            minWidth: 0,
          }}
        >
          {progress}
        </span>
      )}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: STRIP_GAP,
          minWidth: 0,
          paddingBlock: 9,
          paddingInline: 11,
        }}
      >
        {lead}
        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flex: "1 1 auto",
            minWidth: 0,
          }}
        >
          {openControl == null ? (
            primaryNode
          ) : (
            <Cbutton
              alignItems={PRIMARY_ALIGN_ITEMS}
              ariaLabel={primary}
              composite
              containerStyle={PRIMARY_CONTROL_STYLE}
              contentStyle={PRIMARY_CONTROL_STYLE}
              disableClickMotion
              disableHoverEffects
              justifyContent={PRIMARY_JUSTIFY_CONTENT}
              onClick={openControl.onOpen}
              padding={0}
              preset={ButtonPreset.Ghost}
              radius={nestedRadius}
              rootAttributes={{
                ...PRIMARY_ROOT_ATTRIBUTES,
                "aria-controls": openControl.paneId,
                "aria-expanded": openControl.expanded,
              }}
              rootRef={primaryTouch.measureRoot}
              rootRole={PRIMARY_ROOT_ROLE}
              rootTabIndex={0}
              style={{ ...PRIMARY_CONTROL_STYLE, ...primaryTouch.style }}
            >
              {primaryNode}
            </Cbutton>
          )}
          {secondary != null && (
            <span
              style={{
                ...CRC_TRUNCATE_STYLE,
                display: "block",
                fontSize: 9.5,
                fontWeight: 540,
                color: CRC_TEXT_MUTED,
              }}
            >
              {secondary}
            </span>
          )}
          {facts != null && facts.length > 0 && (
            <CresultcardFactStrip
              facts={facts}
              max={facts.length}
            />
          )}
        </div>
        {figure}
        {trailing}
      </div>
    </div>
  );
}
